import {
  normalizeCvExperienceContent,
  normalizeCvExperienceMetadata,
  normalizeCvTextFields,
} from "@tabbio/utils/cv-content-normalization";
import type { ExperienceLocationType, ParsedCvData } from "@tabbio/types";
import {
  formatYearMonthForPicker,
  normalizeCredentialUrl,
  splitCvPeriodRange,
  stripHtmlFromPlainText,
} from "@tabbio/utils";
import { isTextCoveredByBullets } from "@tabbio/utils/cv-dedupe";
import { normalizeCvLanguageProficiency } from "@tabbio/utils/cv-language-proficiency";
import { normalizeCvLinkValue } from "@tabbio/utils/cv-profile-links";
import { composeEducationDescription } from "@tabbio/utils/education-description";
import type { CVProfile } from "./cv-mock-data";

// ─── Date Formatting ───────────────────────────────────────────

/**
 * Convert any loosely-formatted API/AI date → the picker's display format
 * ("Jan 2023" or "2023"). Delegates parsing to the shared
 * `formatYearMonthForPicker` so day-inclusive, dash, slash and named-month
 * shapes the AI emits all resolve to the same month/year the manual picker
 * produces: instead of leaking the raw string through and rendering
 * "Invalid date" (issues #175/#178/#181). Anything unparseable becomes ""
 * (empty, user-fillable) rather than garbage.
 */
export function formatDateForPicker(dateStr?: string | null): string {
  return formatYearMonthForPicker(dateStr);
}

/**
 * Conventional role names for engagement-type-only CV entries ("Volunteer:",
 * "Training:", "Internship Training") whose extraction leaves `title` empty.
 */
const EMPLOYMENT_TYPE_ROLES: Record<string, string> = {
  internship: "Intern",
  "apprenticeship/trainee": "Trainee",
  volunteer: "Volunteer",
  freelance: "Freelancer",
};

export function deriveRoleFromEmploymentType(
  employmentType?: string | null,
): string {
  if (!employmentType) return "";
  return EMPLOYMENT_TYPE_ROLES[employmentType.trim().toLowerCase()] ?? "";
}

/** Combine start/end dates into a period string: "Jan 2023 - Present" */
export function formatPeriod(
  startDate?: string | null,
  endDate?: string | null,
  current?: boolean,
): string {
  const start = formatDateForPicker(startDate);
  if (current && !start) return "Present";
  const end = current ? "Present" : formatDateForPicker(endDate);
  if (start && end && start !== end) return `${start} - ${end}`;
  if (start && end) return start;
  // No end date and not explicitly current: a year-only CV entry ("Volunteer
  // 2013"), NOT an ongoing role. Rendering "2013 - Present" here fabricated a
  // decade of tenure on imported CVs.
  if (start) return start;
  if (end) return `- ${end}`;
  return "";
}

/** Format certification date: "Issued Jun 2020" or "2020" */
export function formatCertDate(dateStr?: string | null): string {
  if (!dateStr) return "";
  const formatted = formatDateForPicker(dateStr);
  if (!formatted) return "";
  return `Issued ${formatted}`;
}

// ─── Phone Formatting ──────────────────────────────────────────

/** Normalize phone: "+92-303-7778-478" → "+923037778478" (digits + leading plus only) */
export function formatPhone(phone?: string | null): string {
  if (!phone) return "";
  // Keep only + (if leading) and digits
  const cleaned = phone.replace(/[^+\d]/g, "");
  // Ensure + is only at the start
  if (cleaned.startsWith("+")) return `+${cleaned.slice(1).replace(/\+/g, "")}`;
  return cleaned;
}

// ─── Language Formatting ───────────────────────────────────────

/**
 * Any known spelling of a level (English phrase, CEFR, or a legacy Arabic
 * editor label such as "مهني") becomes its canonical stored code (B12);
 * unknown text passes through for the API boundary to judge.
 */
export function normalizeLanguageProficiency(proficiency: string): string {
  return normalizeCvLanguageProficiency(proficiency) || proficiency;
}

/** "Arabic – Mother Language" → "Arabic (Native)" */
export function formatLanguage(lang: string): string {
  const parts = lang.split(/\s*[–\u2014-]\s*/);
  if (parts.length >= 2) {
    const proficiency = normalizeLanguageProficiency(
      parts.slice(1).join(" ").trim(),
    );
    return `${parts[0].trim()} (${proficiency})`;
  }
  return lang;
}

// ─── UUID Generation ───────────────────────────────────────────

export function generateId(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

// ─── Experience Overview/Bullets Split ─────────────────────────

/**
 * Preserve the source list structure regardless of bullet length.
 */
function mapExperienceContent(
  description?: string | null,
  achievements?: string[],
): { overview: string; bullets: string[] } {
  const content = normalizeCvExperienceContent(description, achievements);
  return isTextCoveredByBullets(content.overview, content.bullets)
    ? { overview: "", bullets: content.bullets }
    : content;
}

// ─── Main Mapping: ParsedCvData → Partial<CVProfile> ───────────

export function mapParsedToCVProfile(parsed: ParsedCvData): Partial<CVProfile> {
  parsed = normalizeCvTextFields(parsed);
  const linkedin = normalizeCvLinkValue("linkedin", parsed.socials?.linkedin);
  const website = normalizeCvLinkValue("website", parsed.socials?.website);
  const github = normalizeCvLinkValue("github", parsed.socials?.github);
  const dribbble = normalizeCvLinkValue("dribbble", parsed.socials?.dribbble);
  const behance = normalizeCvLinkValue("behance", parsed.socials?.behance);

  return {
    // Basic info
    ...(parsed.name ? { name: parsed.name } : {}),
    ...(parsed.title ? { headline: parsed.title } : {}),
    ...(parsed.summary ? { summary: parsed.summary } : {}),
    ...(parsed.location ? { location: parsed.location } : {}),
    ...(parsed.email ? { contactEmail: parsed.email } : {}),
    ...(parsed.phone ? { phone: formatPhone(parsed.phone) } : {}),
    ...(linkedin ? { linkedin } : {}),
    ...(website ? { website } : {}),
    ...(github ? { github } : {}),
    ...(dribbble ? { dribbble } : {}),
    ...(behance ? { behance } : {}),

    // Languages: format proficiency
    ...(parsed.languages?.length
      ? { languages: parsed.languages.map(formatLanguage) }
      : {}),

    // Skills → skillGroups (split into Professional + Technical if many)
    ...(parsed.skills?.length
      ? {
          skillGroups:
            parsed.skills.length > 12
              ? splitSkillGroups(parsed.skills)
              : [{ category: "Skills", items: parsed.skills }],
        }
      : {}),

    // Experience: map field names + format dates into period
    ...(parsed.experience?.length
      ? {
          experience: parsed.experience.map((source) => {
            const exp = normalizeCvExperienceMetadata(source);
            const { overview, bullets } = mapExperienceContent(
              exp.description,
              exp.achievements,
            );
            return {
              id: generateId(),
              // Entries whose CV shows only an engagement label ("Volunteer:",
              // "Training:") parse with an empty title: fall back to the
              // detected employment type so the card isn't blank and the
              // required-role validation can pass.
              role:
                exp.title || deriveRoleFromEmploymentType(exp.employmentType),
              company: exp.company,
              location: exp.location || "",
              locationType: exp.locationType || "",
              employmentType: exp.employmentType || "",
              period: formatPeriod(exp.startDate, exp.endDate, exp.current),
              overview,
              bullets,
            };
          }),
        }
      : {}),

    // Education: map field names + format year
    ...(parsed.education?.length
      ? {
          education: parsed.education.map((edu) => ({
            id: generateId(),
            degree: edu.degree || "",
            school: edu.school,
            year: formatDateForPicker(edu.endDate),
            startDate: edu.startDate ?? null,
            description: composeEducationDescription({
              leadingText: edu.fieldOfStudy,
              grade: edu.grade,
              description: edu.description,
              details: edu.details,
            }),
          })),
        }
      : {}),

    ...(parsed.featuredProjects?.length
      ? {
          projects: parsed.featuredProjects.map((project) => ({
            id: generateId(),
            title: project.title,
            description: project.description,
            link: project.link,
            technologies: project.technologies ?? [],
          })),
        }
      : {}),

    // Certifications: format date
    ...(parsed.certifications?.length
      ? {
          certifications: parsed.certifications.map((cert) => ({
            id: generateId(),
            name: cert.name,
            issuer: cert.issuer,
            date: formatCertDate(cert.issueDate),
            credentialUrl: cert.credentialUrl || undefined,
          })),
        }
      : {}),

    // Highlights: rename description→desc
    ...(parsed.careerHighlights?.length
      ? {
          highlights: parsed.careerHighlights.map((hl) => ({
            title: hl.title,
            desc: hl.description,
          })),
        }
      : {}),
  } as Partial<CVProfile>;
}

// ─── Split Skills into Professional + Technical ────────────────

const TECHNICAL_KEYWORDS = [
  "react",
  "vue",
  "angular",
  "node",
  "python",
  "java",
  "sql",
  "mongo",
  "postgres",
  "aws",
  "docker",
  "kubernetes",
  "git",
  "api",
  "graphql",
  "firebase",
  "redis",
  "typescript",
  "javascript",
  "css",
  "html",
  "swift",
  "kotlin",
  "flutter",
  "dart",
  "shopify",
  "wordpress",
  "linux",
  "ci/cd",
  "cicd",
  "jest",
  "xcode",
  "android",
  "ios",
  "express",
  "next",
  "nuxt",
  "remix",
  "redux",
  "socket",
  "webpack",
  "supabase",
  "digital",
  "electron",
  "blockchain",
  "webrtc",
  "sequelize",
  "mongoose",
  "swagger",
  "postman",
  "bubble",
  "iis",
];

function splitSkillGroups(
  skills: string[],
): { category: string; items: string[] }[] {
  const technical: string[] = [];
  const professional: string[] = [];

  for (const skill of skills) {
    const lower = skill.toLowerCase();
    if (TECHNICAL_KEYWORDS.some((kw) => lower.includes(kw))) {
      technical.push(skill);
    } else {
      professional.push(skill);
    }
  }

  const groups: { category: string; items: string[] }[] = [];
  if (technical.length > 0)
    groups.push({ category: "Technical", items: technical });
  if (professional.length > 0)
    groups.push({ category: "Professional", items: professional });
  if (groups.length === 0) groups.push({ category: "Skills", items: skills });
  return groups;
}

// ════════════════════════════════════════════════════════════════
// REVERSE MAPPING: CVProfile → Backend API shapes
// ════════════════════════════════════════════════════════════════

const MONTH_MAP: Record<string, string> = {
  Jan: "01",
  Feb: "02",
  Mar: "03",
  Apr: "04",
  May: "05",
  Jun: "06",
  Jul: "07",
  Aug: "08",
  Sep: "09",
  Oct: "10",
  Nov: "11",
  Dec: "12",
};

/** Parse picker values at their stated precision: "Jan 2023" -> "2023-01", "2023" -> "2023". */
export function parsePickerDate(str: string): string | null {
  if (!str) return null;
  const trimmed = str.trim();

  // "Jan 2023" format
  const match = trimmed.match(/^([A-Za-z]{3})\s+(\d{4})$/);
  if (match) {
    const month = MONTH_MAP[match[1]];
    if (month) return `${match[2]}-${month}`;
  }

  // "2023" year-only
  if (/^\d{4}$/.test(trimmed)) return trimmed;

  // Not the picker's own shape: fall back to the shared loose normalizer so a
  // stored "Mar-2015" or "01/2022" still reaches the API as a real date rather
  // than being dropped on the way out.
  const normalized = formatYearMonthForPicker(trimmed);
  if (!normalized || normalized === trimmed) return null;
  return parsePickerDate(normalized);
}

/** Parse period string "Jan 2023 - Present" → { startDate, endDate, current } */
export function parsePeriodString(period: string): {
  startDate: string | null;
  endDate: string | null;
  current: boolean;
} {
  const { start, end, current } = splitCvPeriodRange(period);
  return {
    startDate: start ? parsePickerDate(start) : null,
    endDate: current || !end ? null : parsePickerDate(end),
    current,
  };
}

/** Map CVProfile header fields → backend PATCH /api/profile shape */
export function mapHeaderToAPI(profile: {
  name?: string;
  headline?: string;
  summary?: string;
  location?: string;
  contactEmail?: string;
  phone?: string;
  linkedin?: string;
  website?: string;
}) {
  return {
    name: profile.name,
    headline: profile.headline,
    summary: profile.summary,
    location: profile.location,
    contactEmail: profile.contactEmail,
    phone: profile.phone,
    linkedin: profile.linkedin,
    website: profile.website,
  };
}

/** Map CVExperience → backend PUT /api/profile/experience shape */
export function mapExperienceToAPI(
  exp: {
    id?: string;
    role: string;
    company: string;
    location?: string;
    locationType?: ExperienceLocationType | "";
    employmentType?: string;
    period: string;
    overview?: string;
    bullets: string[];
  },
  order: number,
) {
  const { startDate, endDate, current } = parsePeriodString(exp.period);
  return {
    id: exp.id,
    company: exp.company,
    title: exp.role,
    location: exp.location || undefined,
    locationType: exp.locationType || undefined,
    employmentType: exp.employmentType || undefined,
    startDate,
    endDate,
    current,
    description: exp.overview
      ? stripHtmlFromPlainText(exp.overview)
      : undefined,
    achievements: exp.bullets.map(stripHtmlFromPlainText).filter(Boolean),
    order,
  };
}

/** Map CVEducation → backend PUT /api/profile/education shape */
export function mapEducationToAPI(
  edu: {
    id?: string;
    degree: string;
    school: string;
    year: string;
    startDate?: string | null;
    description?: string;
  },
  order: number,
) {
  return {
    id: edu.id,
    school: edu.school,
    degree: edu.degree || undefined,
    description: edu.description || undefined,
    startDate: edu.startDate,
    endDate: parsePickerDate(edu.year),
    order,
  };
}

/** Map CVCertification → backend PUT /api/profile/certification shape */
export function mapCertificationToAPI(
  cert: {
    id?: string;
    name: string;
    issuer: string;
    date?: string;
    credentialUrl?: string;
  },
  order: number,
) {
  // Parse "Issued Jun 2020" -> "2020-06", preserving month precision.
  const dateStr = (cert.date || "").replace(/^Issued\s+/i, "").trim();
  return {
    id: cert.id,
    name: cert.name,
    issuer: cert.issuer,
    issueDate: parsePickerDate(dateStr),
    credentialUrl: normalizeCredentialUrl(cert.credentialUrl),
    order,
  };
}

/** Map CVHighlight → backend PUT /api/profile/highlight shape */
export function mapHighlightToAPI(
  hl: { id?: string; title: string; desc?: string; description?: string },
  order: number,
) {
  return {
    id: hl.id,
    title: hl.title,
    description: hl.desc || hl.description || "",
    order,
  };
}

/**
 * Parse "Arabic (Native)" → { language: "Arabic", proficiency: "Native" }.
 * A bare "Arabic" keeps an empty proficiency: the user never chose a level, so
 * none is invented (B12).
 */
export function parseLanguageString(lang: string): {
  language: string;
  proficiency: string;
} {
  const match = lang.match(/^(.+?)\s*\((.+)\)\s*$/);
  if (match) {
    return {
      language: match[1].trim(),
      proficiency: normalizeLanguageProficiency(match[2].trim()),
    };
  }
  return { language: lang.trim(), proficiency: "" };
}

/** Map languages string array → backend shape */
export function mapLanguagesToAPI(
  languages: string[],
): { language: string; proficiency: string }[] {
  return languages.map(parseLanguageString);
}

// ─── Recruiter-matching language selection (onboarding "Help Recruiters Match
// You" step): pure helpers, unit-tested in recruiter-languages.test.ts. The
// selected subset lives in privateInfo, separate from the public CV languages.

/** Bare lowercased language name, used for case/proficiency-insensitive matching. */
export function languageNameKey(lang: string): string {
  return parseLanguageString(lang).language.toLowerCase();
}

/**
 * Seed the recruiter selection: `undefined` (never saved) defaults to all
 * available options; an empty array means the user explicitly deselected
 * everything and is preserved as-is.
 */
export function seedRecruiterLanguages(
  saved: string[] | undefined,
  options: string[],
): string[] {
  return saved ?? options;
}

/** True if `lang` (matched by name) is in the selected list, ignoring proficiency/case. */
export function isLanguageSelected(selected: string[], lang: string): boolean {
  return selected.some(
    (item) => languageNameKey(item) === languageNameKey(lang),
  );
}

/**
 * Toggle a language by name: add the full entry (keeping its proficiency) when
 * absent, or remove the name-matching entry when present.
 */
export function toggleRecruiterLanguage(
  selected: string[],
  lang: string,
): string[] {
  return isLanguageSelected(selected, lang)
    ? selected.filter((item) => languageNameKey(item) !== languageNameKey(lang))
    : [...selected, lang];
}
